import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PercentIcon from '@mui/icons-material/Percent';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { mainApi } from '../../../config/apiClient';
import { PAYMENT_SERVICE_URL } from '../../../config/api';
import GuideButton from '../../common/guide/GuideButton';

interface MokaPaymentSettingsProps {
  companyId: string | null;
}

interface MokaSettings {
  installmentNumber: number; // 0 = musteri secer, 2-12 = sabit taksit
  installmentList: number[]; // [] = tum taksitler
  showInstallmentTable: boolean;
  includeCommissionInAmount: boolean;
  interestFreeUpToInstallment: number; // 0 = kapali, 1-11 = ilk N taksit vade farksiz
  showPaymentReceipt: boolean; // odeme sonrasi musteriye dekont butonu
  sendBuyerFullName: boolean;
  sendBuyerEmail: boolean;
  /** true: Ad-Soyad, e-posta ve açıklama Moka isteğine konmaz. */
  onlyRequiredFields: boolean;
  registeredSiteUrl: string;
}

interface MokaIntegration {
  id: number;
  branchId: string | null;
  isEnabled: boolean;
  isTestMode: boolean;
  is3DEnabled: boolean;
  clientCode: string | null;
  clientUsername: string | null;
  clientPassword: string | null;
  settingsJson: string | null;
}

interface MokaCommissionRow {
  bank: string;
  group: string;
  creditType?: string | null;
  cashRate?: number | null;
  installmentRates?: Record<string, number | null>;
}

interface MokaCommissionTable {
  success: boolean;
  failureReason?: string | null;
  source?: string | null;
  testMode?: boolean;
  rows?: MokaCommissionRow[];
}

const INSTALLMENT_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const INSTALLMENT_COLUMNS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const DEFAULT_SETTINGS: MokaSettings = {
  installmentNumber: 0,
  installmentList: [],
  showInstallmentTable: true,
  includeCommissionInAmount: false,
  interestFreeUpToInstallment: 0,
  showPaymentReceipt: false,
  sendBuyerFullName: false,
  sendBuyerEmail: false,
  onlyRequiredFields: true,
  registeredSiteUrl: '',
};

const formatRate = (rate?: number | null) =>
  rate == null || Number.isNaN(rate)
    ? '—'
    : `%${rate.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const parseSettings = (json: string | null): MokaSettings => {
  if (!json) return { ...DEFAULT_SETTINGS };
  try {
    const raw = JSON.parse(json) as Partial<MokaSettings>;
    return {
      installmentNumber:
        typeof raw.installmentNumber === 'number' && raw.installmentNumber >= 2 && raw.installmentNumber <= 12
          ? raw.installmentNumber
          : 0,
      installmentList: Array.isArray(raw.installmentList)
        ? raw.installmentList.filter((n) => Number.isInteger(n) && n >= 1 && n <= 12)
        : [],
      showInstallmentTable: raw.showInstallmentTable !== false,
      includeCommissionInAmount: raw.includeCommissionInAmount === true,
      interestFreeUpToInstallment:
        typeof raw.interestFreeUpToInstallment === 'number' &&
        raw.interestFreeUpToInstallment >= 1 &&
        raw.interestFreeUpToInstallment <= 11
          ? raw.interestFreeUpToInstallment
          : 0,
      showPaymentReceipt: raw.showPaymentReceipt === true,
      sendBuyerFullName: raw.sendBuyerFullName === true,
      sendBuyerEmail: raw.sendBuyerEmail === true,
      onlyRequiredFields: raw.onlyRequiredFields !== false,
      registeredSiteUrl: typeof raw.registeredSiteUrl === 'string' ? raw.registeredSiteUrl.trim() : '',
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

const MokaPaymentSettings: React.FC<MokaPaymentSettingsProps> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const { branches, userDetails } = useAuth();
  const effectiveCompanyId = companyId || userDetails?.companyId || undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integrations, setIntegrations] = useState<MokaIntegration[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<MokaIntegration | null>(null);
  const [settings, setSettings] = useState<MokaSettings>({ ...DEFAULT_SETTINGS });

  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [commission, setCommission] = useState<MokaCommissionTable | null>(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const branchLabel = (branchId: string | null) => {
    if (!branchId) return tt('Şirket Geneli (tüm şubeler)', 'Company-wide (all branches)');
    const b = branches.find((x) => x.id === branchId);
    return b?.name || `#${branchId}`;
  };

  // Moka entegrasyon kayitlarini yukle (sirket-geneli + sube-ozel).
  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await mainApi.get('/api/paymentproviderintegration/list', {
        params: { provider: 'moka', pageSize: 100 },
      });
      const result = resp.data;
      const rows: any[] = result?.Data || result?.data || [];
      const mapped: MokaIntegration[] = rows.map((d) => ({
        id: d.Id ?? d.id,
        branchId: (d.BranchId ?? d.branchId) || null,
        isEnabled: !!(d.IsEnabled ?? d.isEnabled),
        isTestMode: !!(d.IsTestMode ?? d.isTestMode),
        is3DEnabled: !!(d.Is3DEnabled ?? d.is3DEnabled),
        clientCode: (d.ClientCode ?? d.clientCode) || null,
        clientUsername: (d.ClientUsername ?? d.clientUsername) || null,
        clientPassword: null, // liste maskeli; detay ile alinir
        settingsJson: null,
      }));
      setIntegrations(mapped);
      // Once sirket-geneli (branchId null) kaydi sec.
      const preferred = mapped.find((m) => !m.branchId) || mapped[0] || null;
      setSelectedId(preferred ? preferred.id : null);
      if (!preferred) {
        setDetail(null);
        setSettings({ ...DEFAULT_SETTINGS });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.Message ||
          err?.response?.data?.message ||
          tt('Moka entegrasyonları yüklenirken hata oluştu.', 'Failed to load Moka integrations.'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // Secilen kaydin tam detayini (SettingsJson + credential) yukle.
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const resp = await mainApi.get(`/api/paymentproviderintegration/${selectedId}`);
        const result = resp.data;
        const d = result?.Data || result?.data;
        if (!d || cancelled) return;
        const mapped: MokaIntegration = {
          id: d.Id ?? d.id,
          branchId: (d.BranchId ?? d.branchId) || null,
          isEnabled: !!(d.IsEnabled ?? d.isEnabled),
          isTestMode: !!(d.IsTestMode ?? d.isTestMode),
          is3DEnabled: !!(d.Is3DEnabled ?? d.is3DEnabled),
          clientCode: (d.ClientCode ?? d.clientCode) || null,
          clientUsername: (d.ClientUsername ?? d.clientUsername) || null,
          clientPassword: (d.ClientPassword ?? d.clientPassword) || null,
          settingsJson: (d.SettingsJson ?? d.settingsJson) || null,
        };
        setDetail(mapped);
        setSettings(parseSettings(mapped.settingsJson));
      } catch (err: any) {
        if (cancelled) return;
        setSnackbar({
          open: true,
          message:
            err?.response?.data?.Message ||
            err?.response?.data?.message ||
            tt('Ayarlar yüklenirken hata oluştu.', 'Failed to load settings.'),
          severity: 'error',
        });
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const setField = <K extends keyof MokaSettings>(field: K, value: MokaSettings[K]) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const installmentMode = settings.installmentNumber >= 2 ? 'fixed' : 'customer';

  const handleModeChange = (mode: string) => {
    if (mode === 'fixed') {
      setSettings((prev) => ({ ...prev, installmentNumber: prev.installmentNumber >= 2 ? prev.installmentNumber : 3 }));
    } else {
      setSettings((prev) => ({ ...prev, installmentNumber: 0 }));
    }
  };

  const interestMode: 'none' | 'all' | 'threshold' =
    settings.interestFreeUpToInstallment > 0 ? 'threshold' : settings.includeCommissionInAmount ? 'all' : 'none';

  const handleInterestModeChange = (mode: string) => {
    if (mode === 'none') {
      setSettings((prev) => ({ ...prev, includeCommissionInAmount: false, interestFreeUpToInstallment: 0 }));
    } else if (mode === 'all') {
      setSettings((prev) => ({ ...prev, includeCommissionInAmount: true, interestFreeUpToInstallment: 0 }));
    } else {
      // threshold: varsayilan 3, vade farki N+1+ icin musteriye yansir
      setSettings((prev) => ({
        ...prev,
        includeCommissionInAmount: true,
        interestFreeUpToInstallment: prev.interestFreeUpToInstallment > 0 ? prev.interestFreeUpToInstallment : 3,
      }));
    }
  };

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = {
        Id: detail.id,
        BranchId: detail.branchId,
        IsEnabled: detail.isEnabled,
        IsTestMode: detail.isTestMode,
        Is3DEnabled: detail.is3DEnabled,
        // Moka credential alanlari korunur (update validasyonu bunlari zorunlu tutar).
        MerchantId: null,
        MerchantKey: null,
        MerchantSalt: null,
        ClientCode: detail.clientCode,
        ClientUsername: detail.clientUsername,
        ClientPassword: detail.clientPassword,
        Guid: null,
        SettingsJson: JSON.stringify(settings),
      };
      const resp = await mainApi.post('/api/paymentproviderintegration/update', payload);
      const result = resp.data;
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || 'save failed');
      }
      // Kaydedilen normalize edilmis SettingsJson'u geri uygula.
      const d = result?.Data || result?.data;
      if (d) {
        const savedJson = (d.SettingsJson ?? d.settingsJson) || null;
        setDetail((prev) => (prev ? { ...prev, settingsJson: savedJson } : prev));
        setSettings(parseSettings(savedJson));
      }
      setSnackbar({ open: true, message: tt('Moka ayarları kaydedildi.', 'Moka settings saved.'), severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.Message ||
          err?.response?.data?.message ||
          err?.message ||
          tt('Moka ayarları kaydedilemedi.', 'Failed to save Moka settings.'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const loadCommissions = async () => {
    if (!effectiveCompanyId) {
      setCommissionError(tt('Şirket bilgisi bulunamadı.', 'Company info not found.'));
      return;
    }
    setCommissionLoading(true);
    setCommissionError(null);
    setCommission(null);
    try {
      const resp = await fetch(`${PAYMENT_SERVICE_URL}/api/payments/moka/commissions`, {
        headers: { 'X-Company-Id': effectiveCompanyId },
      });
      const json = (await resp.json().catch(() => null)) as MokaCommissionTable | null;
      if (!resp.ok || !json?.success) {
        const baseReason =
          json?.failureReason ||
          (json as { message?: string } | null)?.message ||
          tt('Komisyon tablosu alınamadı.', 'Failed to fetch commission table.');
        // Oranlar Moka bayi hesabında tanımlıdır; test hesabında çoğunlukla tanımsızdır.
        const testHint = json?.testMode
          ? tt(
              ' (Entegrasyon test modunda. Banka taksit oranları genelde canlı Moka bayi hesabında tanımlıdır; test modunda boş dönmesi normaldir.)',
              ' (Integration is in test mode. Bank installment rates are usually defined on the live Moka dealer account; an empty result in test mode is expected.)'
            )
          : '';
        setCommissionError(baseReason + testHint);
        return;
      }
      setCommission(json);
      if (!json.rows || json.rows.length === 0) {
        setCommissionError(
          tt(
            'Moka bayi hesabınızda tanımlı banka taksit komisyon oranı bulunamadı.',
            'No bank installment commission rates are defined on your Moka dealer account.'
          )
        );
      }
    } catch {
      setCommissionError(tt('Moka komisyon servisine bağlanılamadı.', 'Could not reach Moka commission service.'));
    } finally {
      setCommissionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (integrations.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {tt(
            'Henüz Moka ödeme entegrasyonu tanımlı değil. Önce Entegrasyonlar > Ödeme bölümünden Moka bilgilerinizi (DealerCode, Username, Password) ekleyin; ardından buradan taksit, vade farkı ve kart formu ayarlarını yönetebilirsiniz.',
            'No Moka payment integration is defined yet. First add your Moka credentials (DealerCode, Username, Password) under Integrations > Payment; then you can manage installment, interest and card form settings here.'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Entegrasyon secimi (birden fazla sube kaydi varsa) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6">{tt('Moka Entegrasyonu', 'Moka Integration')}</Typography>
          {detail?.isTestMode && (
            <Chip size="small" color="warning" label={tt('Test Modu', 'Test Mode')} />
          )}
          <GuideButton guideKey="integrations.mokaSettings" size="small" />
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" disabled={integrations.length <= 1}>
              <InputLabel id="moka-integration-label">{tt('Kayıt (Şube)', 'Record (Branch)')}</InputLabel>
              <Select
                labelId="moka-integration-label"
                value={selectedId != null ? String(selectedId) : ''}
                label={tt('Kayıt (Şube)', 'Record (Branch)')}
                onChange={(e) => setSelectedId(Number(e.target.value))}
              >
                {integrations.map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>
                    {branchLabel(m.branchId)}
                    {!m.isEnabled ? ` — ${tt('pasif', 'disabled')}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              {tt('Bayi Kodu', 'Dealer Code')}: {detail?.clientCode || '—'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6">{tt('Kayıtlı Web Sitesi', 'Registered Website')}</Typography>
        </Box>
        <TextField
          fullWidth
          size="small"
          label={tt('Moka’da tanımlı site adresi', 'Site URL registered at Moka')}
          placeholder="https://www.ornek.com"
          value={settings.registeredSiteUrl}
          onChange={(e) => setField('registeredSiteUrl', e.target.value.trim())}
          helperText={tt(
            'Moka API bilgileri yalnızca bu site için geçerliyse doldurun. Ödeme başka bir adreste başlarsa müşteri bu sitedeki güvenli ödeme sayfasına yönlendirilir. Vitrin zaten bu adreste ise boş bırakılabilir.',
            'Fill this in when the Moka API credentials are valid only for this site. If payment starts on another address, the customer is sent to the secure payment page on this site. Leave empty when the storefront already runs on that address.'
          )}
        />
      </Paper>

      {/* Taksit Secenekleri */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CreditCardIcon color="primary" />
          <Typography variant="h6">{tt('Taksit Seçenekleri', 'Installment Options')}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel id="moka-inst-mode-label">{tt('Taksit Modu', 'Installment Mode')}</InputLabel>
              <Select
                labelId="moka-inst-mode-label"
                value={installmentMode}
                label={tt('Taksit Modu', 'Installment Mode')}
                onChange={(e) => handleModeChange(String(e.target.value))}
              >
                <MenuItem value="customer">
                  {tt('Müşteri taksit seçsin (önerilen)', 'Customer chooses installments (recommended)')}
                </MenuItem>
                <MenuItem value="fixed">{tt('Sabit taksit dayat', 'Force a fixed installment')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {installmentMode === 'fixed' ? (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="moka-fixed-inst-label">{tt('Taksit Sayısı', 'Installment Count')}</InputLabel>
                <Select
                  labelId="moka-fixed-inst-label"
                  value={String(settings.installmentNumber)}
                  label={tt('Taksit Sayısı', 'Installment Count')}
                  onChange={(e) => setField('installmentNumber', Number(e.target.value))}
                >
                  {INSTALLMENT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n} {tt('taksit', 'installments')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={7}>
                <FormControl fullWidth size="small">
                  <InputLabel id="moka-inst-list-label">
                    {tt('Gösterilecek Taksitler (boş = tümü)', 'Shown installments (empty = all)')}
                  </InputLabel>
                  <Select
                    labelId="moka-inst-list-label"
                    multiple
                    value={settings.installmentList.map(String)}
                    input={<OutlinedInput label={tt('Gösterilecek Taksitler (boş = tümü)', 'Shown installments (empty = all)')} />}
                    renderValue={(selected) =>
                      (selected as string[]).length === 0
                        ? tt('Tümü', 'All')
                        : (selected as string[])
                            .map((s) => (s === '1' ? tt('Peşin', 'Single') : `${s}`))
                            .join(', ')
                    }
                    onChange={(e) => {
                      const val = e.target.value as string[];
                      setField(
                        'installmentList',
                        val.map(Number).sort((a, b) => a - b)
                      );
                    }}
                  >
                    <MenuItem value="1">
                      <Checkbox checked={settings.installmentList.includes(1)} />
                      <ListItemText primary={tt('Peşin (tek çekim)', 'Single payment')} />
                    </MenuItem>
                    {INSTALLMENT_OPTIONS.map((n) => (
                      <MenuItem key={n} value={String(n)}>
                        <Checkbox checked={settings.installmentList.includes(n)} />
                        <ListItemText primary={`${n} ${tt('taksit', 'installments')}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showInstallmentTable}
                      onChange={(e) => setField('showInstallmentTable', e.target.checked)}
                    />
                  }
                  label={tt(
                    'Ödeme ekranında banka taksit tablosunu göster (kart numarasına göre)',
                    'Show bank installment table on the payment screen (based on card number)'
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Vade Farki */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PercentIcon color="primary" />
          <Typography variant="h6">{tt('Vade Farkı (Komisyon)', 'Interest / Commission')}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <FormControl fullWidth size="small">
              <InputLabel id="moka-interest-mode-label">{tt('Vade Farkı Modu', 'Interest Mode')}</InputLabel>
              <Select
                labelId="moka-interest-mode-label"
                value={interestMode}
                label={tt('Vade Farkı Modu', 'Interest Mode')}
                onChange={(e) => handleInterestModeChange(String(e.target.value))}
              >
                <MenuItem value="none">
                  {tt('Vade farkını mağaza üstlenir (müşteriye yansımaz)', 'Store absorbs interest (not passed to customer)')}
                </MenuItem>
                <MenuItem value="all">
                  {tt('Tüm taksitlerde vade farkı müşteriye yansır', 'Interest passed to customer on all installments')}
                </MenuItem>
                <MenuItem value="threshold">
                  {tt('Belirli takside kadar vade farksız, sonrası müşteriye', 'Interest-free up to N installments, rest to customer')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {interestMode === 'threshold' && (
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel id="moka-interest-free-label">{tt('Vade Farksız Taksit Sayısı', 'Interest-free Installments')}</InputLabel>
                <Select
                  labelId="moka-interest-free-label"
                  value={String(settings.interestFreeUpToInstallment || 3)}
                  label={tt('Vade Farksız Taksit Sayısı', 'Interest-free Installments')}
                  onChange={(e) => setField('interestFreeUpToInstallment', Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {tt(`${n} taksite kadar vade farksız`, `Interest-free up to ${n} installments`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        {interestMode === 'none' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {tt(
              'Vade farkı müşteriye yansıtılmaz; komisyonu mağaza üstlenir.',
              'Interest is not passed to the customer; the store absorbs the commission.'
            )}
          </Alert>
        )}
        {interestMode === 'all' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {tt(
              'Taksitli ödemelerde banka komisyonu müşteriden tahsil edilen tutara eklenir.',
              'For installment payments, the bank commission is added to the amount charged to the customer.'
            )}
          </Alert>
        )}
        {interestMode === 'threshold' && (
          <>
            <Alert severity="info" sx={{ mt: 2 }}>
              {tt(
                `İlk ${settings.interestFreeUpToInstallment || 3} taksit vade farksızdır; ${(settings.interestFreeUpToInstallment || 3) + 1}+ taksitte vade farkı müşteriye yansır.`,
                `The first ${settings.interestFreeUpToInstallment || 3} installments are interest-free; interest is passed to the customer from ${(settings.interestFreeUpToInstallment || 3) + 1}+ installments.`
              )}
            </Alert>
            {installmentMode === 'fixed' ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                {tt(
                  'Sabit taksit modunda eşik otomatik ve kesin uygulanır; ek yapılandırma gerekmez.',
                  'In fixed-installment mode the threshold is applied automatically and exactly; no extra configuration is needed.'
                )}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {tt(
                  `Dikkat: "Müşteri taksit seçsin" modunda vade farksızlık Moka bayi panelinize bağlıdır. İlk ${settings.interestFreeUpToInstallment || 3} taksitin gerçekten vade farksız olması için Moka bayi panelinizde bu taksitlerin komisyonu %0 tanımlı OLMALIDIR; aksi halde müşteriden ilk taksitlerde de vade farkı tahsil edilir. Kesin kontrol istiyorsanız "Sabit taksit dayat" modunu kullanın.`,
                  `Warning: in "customer chooses" mode, interest-free behavior depends on your Moka dealer panel. For the first ${settings.interestFreeUpToInstallment || 3} installments to be truly interest-free, the commission for these installments MUST be set to 0% in your Moka dealer panel; otherwise the customer will be charged interest on the early installments too. For guaranteed control, use "Force a fixed installment" mode.`
                )}
              </Alert>
            )}
          </>
        )}
      </Paper>

      {/* Odeme Sonrasi Dekont */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h6">{tt('Ödeme Sonrası Dekont', 'Post-payment Receipt')}</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={settings.showPaymentReceipt}
              onChange={(e) => setField('showPaymentReceipt', e.target.checked)}
            />
          }
          label={tt(
            'Ödeme başarılı ekranında müşteriye "Dekontu indir/yazdır" butonu göster',
            'Show a "Download/print receipt" button to the customer on the payment success screen'
          )}
        />
        <Alert severity="info" sx={{ mt: 1 }}>
          {tt(
            'Dekont bilgi amaçlıdır; resmî mali belge (fatura/fiş) yerine geçmez.',
            'The receipt is for information only; it is not a legal financial document (invoice/receipt).'
          )}
        </Alert>
      </Paper>

      {/* Kart Formu Alanlari */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6">{tt('Kart Formu Alanları', 'Card Form Fields')}</Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={settings.onlyRequiredFields}
              onChange={(e) => setField('onlyRequiredFields', e.target.checked)}
            />
          }
          label={tt(
            'Sadece zorunlu alanlar (TC, Ad-Soyad ve açıklama gönderilmesin)',
            'Required fields only (do not send national ID, full name, or description)'
          )}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.sendBuyerFullName}
                disabled={settings.onlyRequiredFields}
                onChange={(e) => setField('sendBuyerFullName', e.target.checked)}
              />
            }
            label={tt(
              'Ad-Soyad bilgisini Moka’ya otomatik gönder',
              'Send full name to Moka automatically'
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.sendBuyerEmail}
                disabled={settings.onlyRequiredFields}
                onChange={(e) => setField('sendBuyerEmail', e.target.checked)}
              />
            }
            label={tt(
              'E-posta bilgisini Moka’ya otomatik gönder',
              'Send e-mail to Moka automatically'
            )}
          />
        </Box>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {tt(
            'Moka ödeme sayfasında yıldızlı alanlar Kart Sahibi, Kart Numarası, Ay/Yıl ve CVC’dir; sadece bunlar kalır. TCKN, Ad-Soyad ve Açıklama kutularını Moka kendi sayfasında çizer. İframe dokümanında bu kutuları gizleyen bir parametre yoktur; bu anahtar yalnızca o değerleri isteğe koymaz, kutuları ekrandan silmez. Kutuların kalkması için Moka destekten bayi hesabında kapatılmalarını isteyin.',
            'On the Moka payment page the starred fields are Card Holder, Card Number, Expiry and CVC; those are the ones that remain. Moka draws the national ID, full name and description boxes on its own page. The iframe docs have no parameter that hides those boxes. This switch only keeps those values out of the request; it does not remove the boxes. Ask Moka support to turn them off on the dealer account.'
          )}
        </Alert>
      </Paper>

      {/* Banka Taksit Oranlari (salt-okunur) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6">{tt('Banka Taksit Oranları', 'Bank Installment Rates')}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tt(
            'Oranlar Moka bayi hesabınıza tanımlıdır ve karttan sizin adınıza kesilen komisyondur. Boş hücre, o taksitin ilgili kart programında kapalı olduğu anlamına gelir.',
            'Rates are defined on your Moka dealer account and represent the commission charged to you. An empty cell means that installment is disabled for the related card program.'
          )}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            void loadCommissions();
          }}
          disabled={commissionLoading}
          startIcon={commissionLoading ? <CircularProgress size={18} /> : <AccountBalanceIcon />}
        >
          {tt('Banka taksit oranlarını getir', 'Fetch bank installment rates')}
        </Button>
        {commissionError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {commissionError}
          </Alert>
        )}
        {commission?.rows && commission.rows.length > 0 && (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Banka', 'Bank')}</TableCell>
                  <TableCell>{tt('Program', 'Program')}</TableCell>
                  <TableCell align="right">{tt('Peşin', 'Single')}</TableCell>
                  {INSTALLMENT_COLUMNS.map((n) => (
                    <TableCell key={n} align="right">
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {commission.rows.map((row, index) => (
                  <TableRow key={`${row.bank}-${row.group}-${index}`}>
                    <TableCell>{row.bank || '—'}</TableCell>
                    <TableCell>{row.group || row.creditType || '—'}</TableCell>
                    <TableCell align="right">{formatRate(row.cashRate)}</TableCell>
                    {INSTALLMENT_COLUMNS.map((n) => (
                      <TableCell key={n} align="right">
                        {formatRate(row.installmentRates?.[String(n)])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !detail}
        >
          {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MokaPaymentSettings;
